import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { FaEllipsisV } from "react-icons/fa";
import DeleteModal from "../modal/DeleteModal";
import ViewUserModal from "../modal/ViewUserModal";
import { request } from "../../../utils/api";
import { toast } from "react-toastify";
import Loading from "../utilComponent/Loading";
import Pagination from "../utilComponent/Pagination";
import { formatYYYYMMDD, showImage } from "../../../utils";

const HeroList = () => {
  const navigate = useNavigate();

  const [heroes, setHeroes] = useState([]);
  const [selectedHero, setSelectedHero] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const getHeroes = async (page = 1, search = searchText) => {
    try {
      setLoading(true);
      const res = await request({
        url: `heroes?page=${page}&per_page=${pagination.per_page}&search=${search}`,
        method: "GET",
      });

      if (res?.status && res?.data) {
        setHeroes(
          res.data.items.map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            timeline: item.timeline,
            image: item.image,
            created_at: formatYYYYMMDD(item.created_at),
          }))
        );
        setPagination(res.data.pagination);
      }
    } catch (err) {
      toast.error("Failed to fetch heroes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHeroes(1);
  }, []);

  const deleteHero = async () => {
    try {
      setLoading(true);
      await request({
        url: `heroes/${selectedHero.id}`,
        method: "DELETE",
      });

      toast.success("Hero deleted successfully");
      setShowDeleteModal(false);
      setSelectedHero(null);
      getHeroes(pagination.current_page);
    } catch {
      toast.error("Cannot delete hero");
    } finally {
      setLoading(false);
    }
  };

const heroFields = [
  { label: "Title", key: "title" },
  {
    label: "Status",
    key: "status",
    render: (value) => (
      <span
        className={`badge ${
          value === "active" ? "bg-success" : "bg-secondary"
        }`}
      >
        {value}
      </span>
    ),
  },
  { label: "Timeline", key: "timeline" },
  { label: "Description", key: "description", isHtml: true },
  {
    label: "Image",
    key: "image",
    isImage: true,
  },
  { label: "Created Date", key: "created_at" },
];


  return (
    <div>
      <ViewUserModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={selectedHero}
        fields={heroFields}
        title="Hero Details"
        onEdit={(item) => navigate(`/hero-update/${item.id}`)}
      />

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deleteHero}
      />

      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Hero</li>
            <li className="breadcrumb-item active">Hero List</li>
          </ol>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
        <div className="container-fluid">
          <div className="card shadow-sm">
            <div className="card-header">
              <h6 className="card-title m-0">Hero List</h6>
            </div>

            <div className="card-body">
              {/* Search */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    className="form-control"
                    placeholder="Search heroes..."
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      getHeroes(1, e.target.value);
                    }}
                  />
                </div>
              </div>

              {loading ? (
                <Loading />
              ) : (
                <div className="row">
                  {heroes.length ? (
                    heroes.map((item) => (
                      <div key={item.id} className="col-xl-4 col-lg-6 mb-4">
                        <div className="card h-100 shadow-sm">
                          {item.image ? (
                              <img
                                src={showImage(item.image.url)}
                                alt={item.title}
                                className="card-img-top"
                                style={{ height: "180px", objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center bg-light"
                                style={{ height: "180px" }}
                              >
                                <span className="text-muted">No Image</span>
                              </div>
                            )}
                          <div className="card-body">
                            <div className="d-flex justify-content-between mb-2">
                              <h6 className="fw-bold">{item.title}</h6>
                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="light"
                                  className="border-0 p-0 shadow-none"
                                >
                                  <FaEllipsisV />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setSelectedHero(item);
                                      setShowViewModal(true);
                                    }}
                                  >
                                    View
                                  </Dropdown.Item>

                                  <Dropdown.Item
                                    onClick={() =>
                                      navigate(`/hero-update/${item.id}`)
                                    }
                                  >
                                    Edit
                                  </Dropdown.Item>

                                  <Dropdown.Divider />

                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                      setSelectedHero(item);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>

                            <div
                              className="text-muted mb-2"
                              style={{ maxHeight: "60px", overflow: "hidden" }}
                              dangerouslySetInnerHTML={{
                                __html: item.description,
                              }}
                            />

                            <p className="mb-1">
                              ⏳ <strong>Timeline:</strong> {item.timeline}
                            </p>

                            <p className="mb-0">
                              📅 <strong>Created:</strong> {item.created_at}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5">
                      No heroes found
                    </div>
                  )}
                </div>
              )}

              {pagination.last_page > 1 && (
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={(page) => getHeroes(page)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroList;